import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
  SerializedError
} from '@reduxjs/toolkit';
import { Status } from '../types';
import {
  THUNK_NAMES,
  getProductById,
  getPricelistCategories,
  getPricelistModifiers
} from './single-product.thunk';
import { Modifier, Product } from '../../types';

interface FieldChangedPayload {
  fieldName: string;
  value: any;
}

type FieldsResetPayload = Array<keyof Product>;

interface statusObject {
  getProductById: Status;
  getPricelistModifiers: Status;
  getPricelistCategories: Status;
}

interface SingleProductState {
  product: Product;
  status: statusObject;
  error: SerializedError | null;
  pricelistModifiers: Modifier[];
  pricelistCategories: Record<string, string[]>;
}

const initialState: SingleProductState = {
  product: {} as Product,
  status: {
    getProductById: 'idle',
    getPricelistCategories: 'idle',
    getPricelistModifiers: 'idle'
  },
  error: null,
  pricelistCategories: {},
  pricelistModifiers: []
};

const emptyProduct: Product = {
  productId: '',
  productName: '',
  displayName: '',
  qbName: '',
  type: '',
  subType: '',
  organic: false,
  sortOrder: 0,
  isInStock: true,
  unit: '',
  unitQuantity: 0,
  price: 0,
  nutritionLabel: '',
  attributes: [],
  shippingWidth: 0,
  shippingLength: 0,
  shippingHeight: 0,
  shippingVolume: 0,
  shippingWeight: 0,
  isPerishable: false,
  hasImage: false,
  availableToOrder: false,
  reasonUnavailable: '',
  availableDays: [],
  availableTimes: [],
  maxQuantity: 0,
  minQuantity: 0,
  upc: '',
  brand: '',
  bottleFee: 0,
  isTableSideOnly: false,
  isInactive: false,
  ingredients: '',
  description: '',
  dateCreated: null,
  dateUpdated: null,
  dateDeleted: null,
  isInstructionsHidden: false,
  isAlcohol: false,
  isPickupOnly: false,
  isTaxable: false,
  taxRate: 0,
  isHidden: false,
  disclaimer: '',
  orderingOptions: [],
  isContainer: false,
  modifiers: [],
  hasModifiers: false,
  unitIncrement: 0,
  orderingProducts: [],
  isOnHomepage: false,
  markedInactiveBySystem: false,
  isAutoAdded: false,
  healthClaims: [],
  sellByQty: false,
  mainImage: '',
  // defaultImage: '',
  avgWeight: 0,
  receiptDisplayName: '',
  receiptAlias: '',
  inStoreLastSoldDate: '',
  homesomeId: '',
  needsSDP: false,
  volume: {
    inStoreVolumeL30: 0,
    onlineVolumeL30: 0,
    totalVolumeL30: 0
  },
  printerId: '',
  membershipDiscount: 0,
  skipScan: false,
  POSLinkedItem: '',
  pricelistId: '',
  pricelistName: '',
  tenantId: '',
  tenantName: '',
  locationId: '',
  locationName: ''
};

const resetProduct = {
  type: '',
  subType: '',
  organic: false,
  isInStock: true,
  isInactive: false,
  isAlcohol: false,
  isPickupOnly: false,
  isTaxable: false,
  isOnHomepage: false,

  pricelistId: '',
  pricelistName: '',
  tenantId: '',
  tenantName: '',
  locationId: '',
  locationName: ''
};
const slice = createSlice({
  name: 'singleProduct',
  initialState,
  reducers: {
    updateProduct(state: SingleProductState, action: PayloadAction<FieldChangedPayload>) {
      (state.product as any)[action.payload.fieldName] = action.payload.value;
    },
    resetFields(state: SingleProductState, action: PayloadAction<FieldsResetPayload>) {
      for (let fieldName of action.payload) {
        (state.product as any)[fieldName] = emptyProduct[fieldName as keyof Product];
      }
    },
    resetAllFields(state: SingleProductState) {
      const fieldList: Array<keyof Product> = [
        'mainImage',
        'displayName',
        'upc',
        'brand',
        'type',
        'subType',
        'unit',
        'minQuantity',
        'maxQuantity',
        'unitQuantity',
        'unitIncrement',
        'price',
        'organic',
        'isInStock',
        'isTaxable',
        'isAlcohol',
        'isInactive',
        'isPickupOnly',
        'sellByQty',
        'isOnHomepage',
        'ingredients',
        'description',
        'disclaimer',
        'modifiers',
        'healthClaims'
      ];
      for (let fieldName in state.product) {
        if (fieldList.includes(fieldName as keyof Product)) {
          (state.product as any)[fieldName as keyof Product] =
            emptyProduct[fieldName as keyof Product];
        }
      }
    },
    resetModifiers(state: SingleProductState) {
      state.pricelistModifiers = [];
    },
    resetSingleProduct(state: SingleProductState) {
      state.product = resetProduct as Product;
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isFulfilled(getProductById, getPricelistCategories, getPricelistModifiers),
        (
          state: SingleProductState,
          action: PayloadAction<Product | Record<string, string[]> | Modifier[]>
        ) => {
          const requestType = action.type.split('/')[1];
          switch (requestType) {
            case THUNK_NAMES.GET_PRODUCT_BY_ID:
              state.status.getProductById = 'idle';
              state.product = action.payload as Product;
              break;
            case THUNK_NAMES.GET_PRICELIST_CATEGORIES:
              state.status.getPricelistCategories = 'idle';
              state.pricelistCategories = action.payload as Record<string, string[]>;
              break;
            case THUNK_NAMES.GET_PRICELIST_MODIFIERS:
              state.status.getPricelistModifiers = 'idle';
              state.pricelistModifiers = action.payload as Modifier[];
              break;
            default:
              break;
          }
          state.error = null;
        }
      )
      .addMatcher(
        isPending(getProductById, getPricelistCategories, getPricelistModifiers),
        (state: SingleProductState, action) => {
          const requestType = action.type.split('/')[1];
          switch (requestType) {
            case THUNK_NAMES.GET_PRODUCT_BY_ID:
              state.status.getProductById = 'loading';
              break;
            case THUNK_NAMES.GET_PRICELIST_CATEGORIES:
              state.status.getPricelistCategories = 'loading';
              break;
            case THUNK_NAMES.GET_PRICELIST_MODIFIERS:
              state.status.getPricelistModifiers = 'loading';
              break;
            default:
              break;
          }
          state.error = null;
        }
      )
      .addMatcher(
        isRejected(getProductById, getPricelistCategories, getPricelistModifiers),
        (state: SingleProductState, action) => {
          const requestType = action.type.split('/')[1];
          switch (requestType) {
            case THUNK_NAMES.GET_PRODUCT_BY_ID:
              state.status.getProductById = 'error';
              break;
            case THUNK_NAMES.GET_PRICELIST_CATEGORIES:
              state.status.getPricelistCategories = 'error';
              break;
            case THUNK_NAMES.GET_PRICELIST_MODIFIERS:
              state.status.getPricelistModifiers = 'error';
              break;
            default:
              break;
          }
          state.error = action.error;
        }
      );
  }
});

export default slice;

export const selectors = {
  selectProduct: (state: SingleProductState) => state.product,
  selectIsModalLoading: (state: SingleProductState) => state.status.getProductById === 'loading',
  selectPricelistCategories: (state: SingleProductState) => state.pricelistCategories,
  selectIsPricelistCategoriesLoading: (state: SingleProductState) =>
    state.status.getPricelistCategories === 'loading',
  selectIsPricelistModifiersLoading: (state: SingleProductState) =>
    state.status.getPricelistModifiers === 'loading',
  selectErrorOccuredInModal: (state: SingleProductState) => state.status.getProductById === 'error',
  selectError: (state: SingleProductState) => state.error,
  selectPricelistModifiers: (state: SingleProductState) => state.pricelistModifiers,
  selectHealthClaims: (state: SingleProductState) => state.product?.healthClaims ?? [],
  selectProductModifiers: (state: SingleProductState) => state.product?.modifiers ?? [],
  selectCurrentTenant: (state: SingleProductState) => state.product.tenantId ?? '',
  selectCurrentLocation: (state: SingleProductState) => state.product.locationId ?? '',
  selectCurrentPricelist: (state: SingleProductState) => state.product.pricelistId ?? ''
};
